/**
 * Accessibility utilities and helpers
 */

// Focus management
export function trapFocus(element: HTMLElement) {
  const focusableElements = element.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  ) as NodeListOf<HTMLElement>;
  
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    }
  }

  element.addEventListener('keydown', handleKeyDown);
  
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
}

// Announce to screen readers
export function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.style.position = 'absolute';
  announcement.style.left = '-10000px';
  announcement.style.width = '1px';
  announcement.style.height = '1px';
  announcement.style.overflow = 'hidden';
  
  document.body.appendChild(announcement);
  announcement.textContent = message;
  
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

// Keyboard navigation helpers
export function handleKeyboardNavigation(
  e: KeyboardEvent,
  actions: {
    onEnter?: () => void;
    onSpace?: () => void;
    onEscape?: () => void;
    onArrowUp?: () => void;
    onArrowDown?: () => void;
    onArrowLeft?: () => void;
    onArrowRight?: () => void;
  }
) {
  switch (e.key) {
    case 'Enter':
      e.preventDefault();
      actions.onEnter?.();
      break;
    case ' ':
      e.preventDefault();
      actions.onSpace?.();
      break;
    case 'Escape':
      e.preventDefault();
      actions.onEscape?.();
      break;
    case 'ArrowUp':
      e.preventDefault();
      actions.onArrowUp?.();
      break;
    case 'ArrowDown':
      e.preventDefault();
      actions.onArrowDown?.();
      break;
    case 'ArrowLeft':
      e.preventDefault();
      actions.onArrowLeft?.();
      break;
    case 'ArrowRight':
      e.preventDefault();
      actions.onArrowRight?.();
      break;
  }
}

// ARIA label generators
export function generateGameAriaLabel(game: { title: string; developer: string; price: number; rating?: number }) {
  const parts = [
    `${game.title}, by ${game.developer}`,
    game.price === 0 ? 'Free to play' : `$${game.price}`,
    game.rating ? `Rated ${game.rating} out of 5` : ''
  ].filter(Boolean);
  
  return parts.join('. ');
}

export function generateButtonAriaLabel(action: string, target?: string) {
  return target ? `${action} ${target}` : action;
}

// Color contrast checker (simplified)
export function hasGoodContrast(color1: string, color2: string): boolean {
  // This is a simplified version - in production, use a proper color contrast library
  const getLuminance = (color: string) => {
    const rgb = parseInt(color.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  };
  
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const contrast = (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
  
  return contrast >= 4.5; // WCAG AA standard
}

// Skip link generator
export function createSkipLinks() {
  const skipLinks = [
    { href: '#main-content', text: 'Skip to main content' },
    { href: '#navigation', text: 'Skip to navigation' },
    { href: '#search', text: 'Skip to search' }
  ];

  const skipLinksContainer = document.createElement('div');
  skipLinksContainer.className = 'skip-links';
  skipLinksContainer.style.cssText = `
    position: absolute;
    top: -40px;
    left: 0;
    background: var(--bg-primary);
    padding: 8px;
    z-index: 9999;
  `;

  skipLinks.forEach(link => {
    const a = document.createElement('a');
    a.href = link.href;
    a.textContent = link.text;
    a.style.cssText = `
      display: block;
      padding: 4px 8px;
      color: var(--text-primary);
      text-decoration: none;
      border: 1px solid var(--border);
      margin-bottom: 4px;
    `;
    a.addEventListener('focus', () => {
      skipLinksContainer.style.top = '0';
    });
    a.addEventListener('blur', () => {
      skipLinksContainer.style.top = '-40px';
    });
    skipLinksContainer.appendChild(a);
  });

  document.body.insertBefore(skipLinksContainer, document.body.firstChild);
}
